import isApiError from "@insite/client-framework/Common/isApiError";
import { get, post, ServiceResult } from "@insite/client-framework/Services/ApiService";
import { CreateQuoteApiParameter, GetQuoteApiParameter } from "@insite/client-framework/Services/QuoteService";
import { QuoteModel } from "@insite/client-framework/Types/ApiModels";

const customQuotesUrl = "/api/v1/customRfq";

export interface CreateQuoteApiResult extends QuoteModel {
    CartId: string;
    Message: string;
}

export async function customCreateQuote(
    createQuoteParameter: CreateQuoteApiParameter,
): Promise<ServiceResult<CreateQuoteApiResult>> {
    try {
        const customQuote = await post<CreateQuoteApiParameter, CreateQuoteApiResult>(
            customQuotesUrl,
            createQuoteParameter,
        );

        return {
            successful: true,
            result: cleanQuote(customQuote),
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function customGetQuote(parameter: GetQuoteApiParameter) {
    const quote = await get<QuoteModel>(`${customQuotesUrl}/getQuote/${parameter.quoteId}?isSpire=true`);
    return cleanQuote(quote);
}

function cleanQuote(quote: QuoteModel) {
    const quoteCleaned = lowerCaseKeys(quote);
    quoteCleaned.orderDate = quoteCleaned.orderDate ? new Date(quoteCleaned.orderDate) : null;
    quoteCleaned.expirationDate = quoteCleaned.expirationDate ? new Date(quoteCleaned.expirationDate) : null;
    quoteCleaned.cartLines = quoteCleaned.quoteLineCollection;
    quoteCleaned.messageCollection?.forEach((message: { createdDate: string | number | Date }) => {
        if (message.createdDate) {
            message.createdDate = new Date(message.createdDate);
        }
    });

    return quoteCleaned;
}

type KeyPair<K extends string, V> = {
    [key in K]: V;
};

export const lowerCaseKeys = (obj: any): any => {
    if (typeof obj !== "object") {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(lowerCaseKeys);
    }

    if (obj === null) {
        return null;
    }

    const entries = Object.entries(obj);

    const mappedEntries = entries.map(([k, v]) => [`${k.slice(0, 1).toLowerCase()}${k.slice(1)}`, lowerCaseKeys(v)]);

    const newObject = {} as KeyPair<string, any>;

    for (const [key, val] of mappedEntries) {
        newObject[key] = val;
    }

    return newObject;
};
